import type { SidebarId } from "@/navigation/types";
import type { Permission } from "@/types";

/**
 * Skeleton layout while a lazy route chunk loads ({@link PageSuspense}).
 * Attach config using {@link skeletonRoute} in the router; use keys like {@link SkeletonVariant.List}.
 */
export const SkeletonVariant = {
  Shell: "shell",
  /** Content-only blocks under mounted chrome (project/settings shells). */
  Embedded: "embedded",
  List: "list",
  Form: "form",
} as const;

/** Union of {@link SkeletonVariant} values. */
export type SkeletonVariantType = (typeof SkeletonVariant)[keyof typeof SkeletonVariant];

/** Config for the lazy-route skeleton; lives under {@link AppRouteHandle.skeleton}. */
export type RouteSkeletonConfig = {
  skeletonVariant?: SkeletonVariantType;
  /** Only when skeletonVariant is {@link SkeletonVariant.List}. */
  skeletonListShowPrimaryAction?: boolean;
  skeletonListShowSearch?: boolean;
};

/**
 * App-specific route `handle` (React Router metadata). Extend this type as you add per-route data;
 * keep skeleton isolated under `skeleton` so fields never collide.
 */
export type AppRouteHandle = {
  skeleton?: RouteSkeletonConfig;
  /** Which sidebar nav scope to render — resolved by {@link useSidebarNav}. */
  sidebar?: SidebarId;
  /** Required permission to render this route (checked by {@link PermissionGate}). */
  permission?: Permission;
};
